import { ExternalLink } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface Props {
  /** Destination. `null`/blank renders nothing — a link with no target is worse
   *  than no link, since it still invites a click. */
  href: string | null | undefined;
  /** Accessible name; also the native tooltip. Should name the destination
   *  ("View in Langfuse"), not the glyph. */
  label: string;
  className?: string | undefined;
  /** `data-testid` for the link (default `external-link-button`). */
  testId?: string;
}

/**
 * Icon-only link out to another system, sized to sit beside `CopyIconButton` in
 * an identity row. Renders `null` when there is no href, so callers can pass a
 * possibly-absent URL without guarding at every call site.
 *
 * Always opens in a new tab: these point at separate tools (Langfuse, Temporal),
 * and navigating away would lose the page's unsaved filter/scroll state.
 */
export function ExternalLinkIconButton({
  href,
  label,
  className,
  testId = 'external-link-button',
}: Props) {
  if (!href || href.trim() === '') return null;

  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className={cn('size-6 shrink-0 cursor-pointer', className)}
    >
      <a
        href={href}
        target="_blank"
        // `noopener` is the security-relevant half (denies the opened tab a
        // handle back to this window via `window.opener`).
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        data-testid={testId}
        onClick={(e) => {
          // These rows sit inside clickable cards and accordion triggers — don't
          // let opening the link also toggle or navigate the parent.
          e.stopPropagation();
        }}
      >
        <ExternalLink className="size-3.5" aria-hidden="true" />
      </a>
    </Button>
  );
}
